import { MouseEventHandler } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { CustomContactFormContext, useCustomContactFormSlidePanel } from '@frontend/create-contact-panel';
import { getFullName } from '@frontend/name';
import { formatPhoneNumber, formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useThreadHeaderContext } from '@frontend/thread-header-context';
import { ThreadTitle, ThreadTitleProps } from '../thread-title';

type TitleProps = Omit<ThreadTitleProps, 'text' | 'personId' | 'groupId'> & {
  onCreateContact?: CustomContactFormContext['onSave'];
  onCreateContactError?: CustomContactFormContext['onError'];
  isCompactMode?: boolean;
};

export const Title = ({
  onClick,
  openContactPanel = true,
  onCreateContact,
  onCreateContactError,
  variant: providedVariant,
  isCompactMode,
  ...props
}: TitleProps) => {
  const {
    personPhone,
    groupId,
    variant,
    primaryContact,
    primaryContactId,
    contextContact,
    contextPersonId,
    associatedContacts,
  } = useThreadHeaderContext();
  const text = contextContact
    ? getFullName(contextContact)
    : primaryContact
    ? getFullName(primaryContact)
    : formatPhoneNumber(personPhone);
  const { setShow } = useCustomContactFormSlidePanel();

  const getHeadOfHousehold = (contacts: Person[]): string | undefined => {
    if (!Array.isArray(contacts)) return undefined;

    const filteredContacts = contacts.filter(
      (contact): contact is Person & { birthdate: string } =>
        contact.isGuardian === true && !!contact.birthdate && contact.personPmid === contact.householdPmid
    );

    if (filteredContacts.length === 0) {
      return undefined;
    }

    const headOfHousehold = filteredContacts.reduce((oldest, current) =>
      new Date(current.birthdate) < new Date(oldest.birthdate) ? current : oldest
    );

    return headOfHousehold.personId;
  };

  const headOfHouseholdId = getHeadOfHousehold(associatedContacts || []);

  const openCreateContact = () => {
    setShow({
      show: true,
      context: {
        mode: 'create',
        person: {
          MobilePhone: formatPhoneNumberE164(personPhone).replace('+1', ''),
        },
        locationId: groupId,
        onSave: onCreateContact,
        onError: onCreateContactError,
      },
    });
  };

  const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    if (openContactPanel && !primaryContact) {
      openCreateContact();
    }
    onClick?.(e);
  };

  const showSecondaryPerson = contextContact && primaryContact && contextContact.personId !== primaryContact?.personId;

  return (
    <ThreadTitle
      css={[openContactPanel && !primaryContact && { cursor: 'pointer' }]}
      {...props}
      text={text}
      avatarProps={{
        name: contextContact || primaryContact ? text : undefined,
        firstName:
          contextContact?.preferredName ||
          contextContact?.firstName ||
          primaryContact?.preferredName ||
          primaryContact?.firstName,
        lastName: contextContact?.lastName ?? primaryContact?.lastName,
      }}
      personId={contextPersonId || primaryContact?.personId || primaryContactId}
      secondaryPerson={
        showSecondaryPerson
          ? {
              personId: primaryContact.personId,
              firstName: primaryContact.firstName ?? '',
              preferredName: primaryContact.preferredName ?? '',
              lastName: primaryContact.lastName ?? '',
            }
          : undefined
      }
      groupId={groupId}
      variant={providedVariant || variant}
      onClick={handleClick}
      openContactPanel={openContactPanel}
      isCompactMode={isCompactMode}
      headOfHouseholdId={associatedContacts && associatedContacts?.length > 1 ? headOfHouseholdId : undefined}
    />
  );
};
